import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAuthUseCase } from "@gui/contexts/useCases/UseCasesContext";
import { Oops } from "@gui/infrastructure/pass-through/oops";
import { Notifications } from "@gui/infrastructure/pass-through/notifications";
import { DEFAULT_PAGE } from "@domain/constants";
import { LoginProps, LoginViewModel } from "./login.types";
import { logger } from "@gui/infrastructure/pass-through/logger";
import { LogData } from "@rogoag/shared-ts";
import { EMAIL_PATTERN } from "../common";

export function useLoginViewModel(props: LoginProps): LoginViewModel {
	const auth = useAuthUseCase();
	const [username, setEmail] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [emailError, setEmailError] = React.useState("");
	const [passwordError, setPasswordError] = React.useState("");
	const [error, setError] = React.useState("");
	const navigate = useNavigate();

	const handleEmailChange = (event: { target: { value: React.SetStateAction<string> } }) => {
		setEmailError("");
		// validate that username is an email with regex
		if (!EMAIL_PATTERN.test(event.target.value.toString())) {
			setEmailError("Invalid email address");
		}

		setEmail(event.target.value);
	};

	const handlePasswordChange = (event: { target: { value: React.SetStateAction<string> } }) => {
		setPassword(event.target.value);
	};

	const handleSubmit = async (event: { preventDefault: () => void }) => {
		const logData: LogData = {
			function: "login.viewmodel.handleSubmit",
			username: username,
		};
		try {
			setError("");
			if (!username) {
				setEmailError("");
				return;
			}
			if (!password) {
				setPasswordError("");
				return;
			}
			setLoading(true);
			event.preventDefault();
			const result = await auth.login(username, password);
			if (result.ok) {
				logger.log("Login successful", logData);
				props.setLoggedIn(result.value);
				navigate(DEFAULT_PAGE);
				return;
			}
			// Known business error variants — toast a specific message + log.
			logger.warning(`Login failed: ${result.error}`, logData);
			switch (result.error) {
				case "invalid_credentials":
					Notifications.error("Incorrect username or password");
					break;
				case "user_not_found":
					Notifications.error("User does not yet exist in our database. Contact support@rogoag.com");
					break;
				case "user_not_registered":
					Notifications.error("User is not registered. Please register the user or contact software@rogoag.com");
					break;
			}
		} catch (error: any) {
			// Unknown / network error path.
			Oops.handleError(error, logData.function, username);
		} finally {
			setLoading(false);
		}
	};

	const navigateToRegister = () => {
		navigate("/register");
	};

	const navigateToForgotPassword = () => {
		navigate("/forgot-password");
	};

	const isSubmitDisabled = loading || !username || !password || !!emailError || !!passwordError;

	return {
		username,
		password,
		loading,
		emailError,
		passwordError,
		error,
		handleEmailChange,
		handlePasswordChange,
		handleSubmit,
		navigateToRegister,
		navigateToForgotPassword,
		isSubmitDisabled,
	};
}
